import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import useReactRouter from 'use-react-router'
import { imageUrlRoot } from 'app/constants'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import CapacitySelectionField from './CapacitySelectionField'
import CustomOptionsFields from './CustomOptionsFields'
import DatabaseEngineSelectionField from './DatabaseEngineSelectionField'

const DB_OPTIONS = [
  {
    name: 'PostgreSQL',
    description: 'Powerful open-source, object relational database',
    logo: `${imageUrlRoot}/databases/postgreSQL.png`,
    versions: ['v16.0', 'v15.2'],
  },
  {
    name: 'MariaDB',
    description: 'Community developed alternative to MySQL.',
    logo: `${imageUrlRoot}/databases/mariaDB.png`,
    versions: ['v16.0', 'v15.2'],
  },
  {
    name: 'MongoDB',
    description: 'Document DB with high availability and scalability.',
    logo: `${imageUrlRoot}/databases/mongoDB.png`,
    versions: ['v16.0', 'v15.2'],
  },
  {
    name: 'Redis',
    description: 'In-memory storage: key-value dB, cache, OR message broker.',
    logo: `${imageUrlRoot}/databases/redis.png`,
    versions: ['v16.0', 'v15.2'],
  },
]

const CONFIG_OPTIONS = [
  { label: 'Replicated, 3-node', value: 3 },
  { label: 'Single Node', value: 1 },
]

const VM_FLAVORS = [
  {
    id: '1',
    name: 'Lowcore-Small',
    vcpus: 2,
    ram: 8,
    disk: 40,
  },
  {
    id: '2',
    name: 'Lowcore-Medium',
    vcpus: 4,
    ram: 16,
    disk: 40,
  },
  {
    id: '3',
    name: 'Highcore-Medium',
    vcpus: 8,
    ram: 32,
    disk: 40,
  },
  {
    id: '4',
    name: 'Highcore-Large',
    vcpus: 16,
    ram: 60,
    disk: 80,
  },
]

const initialValues = {
  database: 'PostgreSQL',
  databaseVersion: 'v16.0',
  replicas: 3,
  storageVolumes: [{ id: '1', storageClass: null, capacity: 8 }],
  enableBackups: true,
  backupSchedule: 'Hourly',
  numBackups: 7,
}

const DatabaseClusterDeploymentModalForm = (props) => {
  const { onClose } = props
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  const handleSubmit = async () => {
    onClose()
  }

  const submitting = false

  return (
    <ModalForm
      {...props}
      open={true}
      title="Deploy A New Cluster"
      maxWidth={1020}
      className={classes.modal}
      onSubmit={handleSubmit}
      submitTitle="Deploy Cluster"
      submitting={submitting}
    >
      {/* Step 1 */}
      <FormFieldSection title="DB Engine" step={1}>
        <div className={classes.formSection}>
          <DatabaseEngineSelectionField
            databases={DB_OPTIONS}
            params={params}
            getParamsUpdater={getParamsUpdater}
          />
        </div>
      </FormFieldSection>

      {/* Step 2 */}
      <FormFieldSection title="Capacity" step={2}>
        <div className={classes.formSection}>
          <CapacitySelectionField
            configs={CONFIG_OPTIONS}
            vmFlavors={VM_FLAVORS}
            params={params}
            getParamsUpdater={getParamsUpdater}
          />
        </div>
      </FormFieldSection>

      {/* Step 3 */}
      <FormFieldSection title="Custom Options" step={3}>
        <div className={classes.formSection}>
          <CustomOptionsFields params={params} getParamsUpdater={getParamsUpdater} />
        </div>
      </FormFieldSection>

      {/* Step 4 */}
      <FormFieldSection title="Final Touches" step={4}>
        <div className={clsx(classes.formSection, classes.finalTouchesSection)}>
          <TextField
            id="clusterName"
            label="Cluster Name"
            value={params.clusterName}
            onChange={getParamsUpdater('clusterName')}
            required
          />
        </div>
      </FormFieldSection>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
    marginLeft: '12px',
    marginTop: theme.spacing(2),
  },
  finalTouchesSection: {
    marginTop: theme.spacing(2),
  },
}))

export default DatabaseClusterDeploymentModalForm
